import React from 'react';
import { useIntl } from 'react-intl';
import { Spinner } from '@aibuild/ui';
import { messages } from './messages';
import { EmptyNote, RetryButton, StatusLayer } from './ViewerStatus.styled';

export type ViewerStatusKind = 'loading' | 'empty' | 'failed';

export interface ViewerStatusProps {
  status: ViewerStatusKind;
  /** Offered with `failed`; without it the failure is only reported. */
  onRetry?: () => void;
  dataTestId?: string;
}

/** What the viewer is doing when it is not showing a part: never a bare black canvas. */
const ViewerStatus: React.FC<ViewerStatusProps> = ({ status, onRetry, dataTestId = 'model-viewer__status' }) => {
  const intl = useIntl();
  if (status === 'empty') {
    return (
      <StatusLayer $blocking={false} data-testid={dataTestId} data-status={status}>
        <EmptyNote>{intl.formatMessage(messages.empty)}</EmptyNote>
      </StatusLayer>
    );
  }
  if (status === 'loading') {
    const label = intl.formatMessage(messages.loading);
    return (
      <StatusLayer $blocking role="status" data-testid={dataTestId} data-status={status}>
        <Spinner label="" />
        <span>{label}</span>
      </StatusLayer>
    );
  }
  return (
    <StatusLayer $blocking role="alert" data-testid={dataTestId} data-status={status}>
      <span>{intl.formatMessage(messages.unavailable)}</span>
      {onRetry && (
        <RetryButton type="button" onClick={onRetry} data-testid={`${dataTestId}-retry`}>
          {intl.formatMessage(messages.retry)}
        </RetryButton>
      )}
    </StatusLayer>
  );
};

export default ViewerStatus;
